"use client"

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react"
import { usePathname, useRouter } from "next/navigation"
import { toast } from "sonner"
import { useTranslations } from "next-intl"
import { SimpleUser } from "sip.js/lib/platform/web"
import { Inviter, SessionState, UserAgent } from "sip.js"
import { useAuthStore } from "@/stores/auth-store"
import { getTelephonyCredentials, getTelephonyProviders } from "@/lib/telephony-api"
import { lookupLeadByPhone } from "@/lib/leads-api"
import {
  connectTelephonySocket,
  disconnectTelephonySocket,
  joinExtension,
  leaveExtension,
  onCallIncoming,
  onCallEnded,
  onCallMissed,
  onTelephonyConnect,
  onTelephonyDisconnect,
  onCampaignAgentConnect,
} from "@/lib/telephony-socket"
import { useMissedCallsStore } from "@/stores/missed-calls-store"
import { useMidCallNotesStore } from "@/stores/mid-call-notes-store"
import type { TelephonyCredentials, IncomingCallInfo, CallEventPayload, CallState, TransferState, TelephonyProviderOption } from "@/types"
import { Button } from "@/components/ui/button"
import { IncomingCallPopup } from "@/components/telephony/incoming-call-popup"
import { PostCallDispositionModal } from "@/components/telephony/post-call-disposition-modal"
import { InCallControls } from "@/components/telephony/in-call-controls"
import { TransferDialog } from "@/components/telephony/transfer-dialog"

type TelephonyStatus = "idle" | "connecting" | "registered" | "error"

interface LastCallContext {
  actorId?: string
  clientSourceId?: string
  uniqueid?: string
  direction?: string
  number?: string
  extension?: string
  leadId?: string
  leadName?: string
  midCallNotes?: string
}

interface TelephonyContextValue {
  status: TelephonyStatus
  callState: CallState
  incomingNumber: string | null
  call: (number: string, leadContext?: { leadId: string; leadName?: string }) => Promise<void>
  hangup: () => Promise<void>
  answer: () => Promise<void>
  hold: () => Promise<void>
  unhold: () => Promise<void>
  onTransfer: () => void
  isReady: boolean
  isMuted: boolean
  isHeld: boolean
  toggleMute: () => void
  incomingCallInfo: IncomingCallInfo | null
  socketConnected: boolean
  showDispositionModal: boolean
  lastCallContext: LastCallContext | null
  dismissDisposition: () => void
  transferState: TransferState | null
  completeAttendedTransfer: () => Promise<void>
  cancelAttendedTransfer: () => Promise<void>
  /** Configured outbound provider options (telephony_providers_list). Empty → feature inert. */
  providers: TelephonyProviderOption[]
  /** Currently selected provider id (sticky per agent). null → no prefix. */
  selectedProviderId: string | null
  /** Active supervisor spy session (WebRTC), or null. Drives the monitoring banner. */
  monitoring: { mode: string; extension: string } | null
  /** Tear down the current spy leg (hangup). Used before switching spy mode. */
  stopMonitoring: () => Promise<void>
  /** Update the in-session provider selection (persistence handled by the selector). */
  setSelectedProviderId: (id: string | null) => void
}

/**
 * "Is the agent in the middle of something?" — no global dirty-form registry
 * exists, so this reads the page: an open dialog/sheet, or a text field that
 * has content and focus (a note, a message, a quote line being typed).
 */
function hasUnsavedWork(): boolean {
  if (typeof document === "undefined") return false
  if (document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]')) return true
  const el = document.activeElement as HTMLElement | null
  if (!el) return false
  if (el instanceof HTMLTextAreaElement) return el.value.trim().length > 0
  if (el instanceof HTMLInputElement) {
    const textual = ["text", "search", "email", "tel", "url", "number", ""].includes(el.type)
    return textual && el.value.trim().length > 0
  }
  return el.isContentEditable && (el.textContent ?? "").trim().length > 0
}

const TelephonyContext = createContext<TelephonyContextValue | null>(null)

export function useTelephony() {
  return useContext(TelephonyContext)
}

export function TelephonyProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore()
  // Clinic-level kill switch: without the flag, never fetch providers, init
  // SIP, or open the telephony socket — a stale sip_extension on a user must
  // not wake any of it on a PBX-less clinic.
  const telephonyEnabled = user?.feature_flags?.telephony_enabled ?? false
  const router = useRouter()
  const pathname = usePathname()
  const pathnameRef = useRef(pathname)
  useEffect(() => {
    pathnameRef.current = pathname
  }, [pathname])
  const t = useTranslations("telephony")
  const tc = useTranslations("common")
  const [status, setStatus] = useState<TelephonyStatus>("idle")
  const [callState, setCallState] = useState<CallState>("idle")
  const [incomingNumber, setIncomingNumber] = useState<string | null>(null)
  const [credentials, setCredentials] = useState<TelephonyCredentials | null>(null)
  const [isMuted, setIsMuted] = useState(false)
  const [isHeld, setIsHeld] = useState(false)
  const [incomingCallInfo, setIncomingCallInfo] = useState<IncomingCallInfo | null>(null)
  // Supervisor spy state (WebRTC). Set when an inbound INVITE carries the
  // MONITOR:<mode>:<ext> display-name marker; drives the monitoring banner
  // instead of the ringing popup. null = not monitoring.
  const [monitoring, setMonitoring] = useState<{ mode: string; extension: string } | null>(null)
  const [socketConnected, setSocketConnected] = useState(false)
  const [legacyShowDisposition, setShowDispositionModal] = useState(false)
  const [legacyCallContext, setLastCallContext] = useState<LastCallContext | null>(null)
  const [pendingDispositions, setPendingDispositions] = useState<LastCallContext[]>([])
  const callbackTasksEnabled = user?.feature_flags?.tasks_enabled === true && user?.feature_flags?.lead_follow_through_enabled === true
  const lastCallContext = callbackTasksEnabled
    ? pendingDispositions.find((context) => context.actorId === user?.id) ?? null
    : legacyCallContext?.actorId === user?.id ? legacyCallContext : null
  const showDispositionModal = callbackTasksEnabled ? lastCallContext !== null : legacyShowDisposition
  const [transferState, setTransferState] = useState<TransferState | null>(null)
  const [showTransferDialog, setShowTransferDialog] = useState(false)
  // Outbound provider picker (sticky per agent). providers empty → feature inert.
  const [providers, setProviders] = useState<TelephonyProviderOption[]>([])
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(
    user?.telephony_provider_id ?? null
  )
  const simpleUserRef = useRef<SimpleUser | null>(null)
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null)
  const callContextRef = useRef<LastCallContext | null>(null)
  const consultationSessionRef = useRef<Inviter | null>(null)
  const transferStateRef = useRef<TransferState | null>(null)

  useEffect(() => {
    callContextRef.current = null
    // Identity changes must discard drafts before another user's calls arrive.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPendingDispositions([])
    setLastCallContext(null)
    setShowDispositionModal(false)
  }, [user?.id])

  // Keep ref in sync so stateChange listeners can read latest transfer state
  useEffect(() => {
    transferStateRef.current = transferState
  }, [transferState])

  const cleanup = useCallback(async () => {
    const simpleUser = simpleUserRef.current
    if (simpleUser) {
      try {
        await simpleUser.unregister()
        await simpleUser.disconnect()
      } catch {
        // ignore
      }
    }
    simpleUserRef.current = null
    consultationSessionRef.current = null
    useMidCallNotesStore.getState().clear()
    setCredentials(null)
    setStatus("idle")
    setCallState("idle")
    setIncomingNumber(null)
    setIsMuted(false)
    setIsHeld(false)
    setIncomingCallInfo(null)
    setMonitoring(null)
    setTransferState(null)
    setShowTransferDialog(false)
  }, [])

  // SIP.js initialization
  useEffect(() => {
    let isActive = true
    const init = async () => {
      if (!telephonyEnabled || !user?.webrtc_enabled) {
        await cleanup()
        return
      }

      setStatus("connecting")
      try {
        const creds = await getTelephonyCredentials()
        if (!isActive) return
        setCredentials(creds)

        const sipDomain = creds.sip_domain
        const sipWsUrl = creds.sip_ws_url
        const sipUsername = creds.user.sip_username
        const sipPassword = creds.user.sip_password
        const turnServers = creds.turn_servers || []
        const turnUsername = creds.turn_username || ""
        const turnCredential = creds.turn_credential || ""
        const iceServers: RTCIceServer[] = []
        ;(creds.stun_servers || []).forEach((url) => {
          iceServers.push({ urls: url })
        })
        if (turnServers.length > 0 && turnUsername && turnCredential) {
          turnServers.forEach((url) => {
            iceServers.push({ urls: url, username: turnUsername, credential: turnCredential })
          })
        }
        const forceRelay = false
        console.log("[Telephony] ICE servers configured:", iceServers.length)

        if (!sipDomain || !sipWsUrl || !sipUsername || !sipPassword) {
          setStatus("error")
          return
        }

        const delegate = {
          onCallReceived: async () => {
            // Supervisor spy legs arrive as a normal inbound INVITE whose caller-id
            // display name is the literal "MONITOR:<mode>:<targetExt>" (shared
            // contract with the telephony-service). Detect via the "MONITOR:" prefix,
            // parse [_, mode, ext], auto-answer silently, and show the monitoring
            // banner instead of the ringing popup. If the display name is empty
            // (some gateways strip it) we fall through to normal ringing behaviour.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const displayName = (simpleUser as any).session?.remoteIdentity?.displayName as
              | string
              | undefined
            if (displayName?.startsWith("MONITOR:")) {
              const [, mode, ext] = displayName.split(":")
              setMonitoring({ mode: mode || "listen", extension: ext || "" })
              // Not a normal call — don't arm the post-call disposition modal.
              callContextRef.current = null
              try {
                await simpleUser.answer()
                setCallState("in-call")
              } catch (error) {
                console.error("[Telephony] Failed to auto-answer spy leg", error)
                setMonitoring(null)
              }
              return
            }
            setCallState("ringing")
            // Do NOT auto-answer — wait for user to click Answer
          },
          onCallAnswered: () => {
            setCallState("in-call")
            setIncomingCallInfo(null) // Clear popup when call is established
          },
          onCallHold: (held: boolean) => {
            setCallState(held ? "on-hold" : "in-call")
            setIsHeld(held)
          },
          onCallHangup: () => {
            // Clean up any active consultation session
            if (consultationSessionRef.current) {
              try {
                consultationSessionRef.current.bye()
              } catch {
                // ignore -- session may already be terminated
              }
              consultationSessionRef.current = null
            }
            // Capture mid-call notes before clearing
            const midCallNotes = useMidCallNotesStore.getState().notes
            useMidCallNotesStore.getState().clear()
            // Trigger disposition modal if we had an active call
            if (callContextRef.current) {
              const completedContext = {
                ...callContextRef.current,
                ...(midCallNotes ? { midCallNotes } : {}),
              }
              const flags = useAuthStore.getState().user?.feature_flags
              if (flags?.tasks_enabled && flags?.lead_follow_through_enabled) {
                setPendingDispositions((pending) => [...pending, completedContext])
              } else {
                setLastCallContext(completedContext)
                setShowDispositionModal(true)
              }
            }
            callContextRef.current = null
            setCallState("idle")
            setIncomingNumber(null)
            setIsMuted(false)
            setIsHeld(false)
            setIncomingCallInfo(null)
            setMonitoring(null)
            setTransferState(null)
            setShowTransferDialog(false)
          },
        }

        const simpleUser = new SimpleUser(sipWsUrl, {
          aor: `sip:${sipUsername}@${sipDomain}`,
          media: {
            constraints: { audio: true, video: false },
            remote: {
              audio: remoteAudioRef.current ?? undefined,
            },
          },
          userAgentOptions: {
            authorizationUsername: sipUsername,
            authorizationPassword: sipPassword,
            logLevel: "error",
            sessionDescriptionHandlerFactoryOptions: {
              iceGatheringTimeout: 10000,
              peerConnectionConfiguration: {
                iceServers,
                iceTransportPolicy: forceRelay ? "relay" : "all",
              },
            },
          },
        })
        simpleUser.delegate = delegate

        simpleUserRef.current = simpleUser
        await simpleUser.connect()
        await simpleUser.register()
        if (!isActive) return
        setStatus("registered")
      } catch (error) {
        console.error(error)
        setStatus("error")
        toast.error(t("telephonyProvider.connectFailed"))
      }
    }

    init()

    return () => {
      isActive = false
      cleanup()
    }
  }, [user?.webrtc_enabled, telephonyEnabled, cleanup, t])

  // Load configured outbound providers once (the provider picker hides itself
  // when this resolves empty — preserving the current no-prefix behavior).
  // Only when authenticated: anonymous contexts (public quote page, the PDF
  // renderer) must not hit the protected settings endpoint — its 401 trips the
  // global redirect-to-login interceptor and hijacks the public page.
  useEffect(() => {
    if (!user || !telephonyEnabled) return
    let active = true
    getTelephonyProviders()
      .then((list) => {
        if (!active) return
        setProviders(list)
        // Seed the selection: stick to the agent's stored default if it still
        // exists in the list, otherwise fall back to the first configured entry.
        setSelectedProviderId((prev) => {
          if (prev && list.some((p) => p.id === prev)) return prev
          const stored = user?.telephony_provider_id ?? null
          if (stored && list.some((p) => p.id === stored)) return stored
          return list.length > 0 ? list[0].id : null
        })
      })
      .catch(() => {
        // Ignore — leave providers empty (feature inert)
      })
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.telephony_provider_id, !!user, telephonyEnabled])

  // Socket.IO integration
  useEffect(() => {
    const extension = user?.sip_extension
    if (!extension || !telephonyEnabled) return

    connectTelephonySocket()

    const cleanupConnect = onTelephonyConnect(() => {
      console.log("[TelephonySocket] Connected")
      setSocketConnected(true)
      joinExtension(extension)
    })

    const cleanupDisconnect = onTelephonyDisconnect((reason) => {
      console.log("[TelephonySocket] Disconnected:", reason)
      setSocketConnected(false)
    })

    const cleanupIncoming = onCallIncoming(async (data: CallEventPayload) => {
      console.log("[TelephonySocket] Incoming call:", data)
      const callerNumber = data.from || "Unknown"

      // Capture this call so a late lookup cannot overwrite another call or identity.
      const sourceId = crypto.randomUUID()
      const actorId = user?.id
      callContextRef.current = {
        clientSourceId: sourceId,
        actorId: user?.id,
        uniqueid: data.uniqueid,
        direction: data.direction,
        number: callerNumber,
        extension: data.extension,
      }

      // Lookup lead by phone
      let leadInfo = null
      let lastCallInfo = null
      try {
        const result = await lookupLeadByPhone(callerNumber)
        if (result) {
          leadInfo = {
            id: result.id,
            first_name: result.first_name,
            last_name: result.last_name,
            full_name: result.full_name,
            phone: result.phone,
          }
          lastCallInfo = result.last_call ? {
            id: result.last_call.id,
            direction: result.last_call.direction as 'inbound' | 'outbound' | 'internal' | null,
            started_at: result.last_call.started_at,
            duration_seconds: result.last_call.duration_seconds,
            pbx_uniqueid: null,
            pbx_linkedid: null,
            from_number: null,
            to_number: null,
            extension: null,
            trunk: null,
            status: null,
            answered_at: null,
            ended_at: null,
            bill_seconds: null,
            hangup_cause: null,
            disposition: null,
            notes: null,
            created_at: result.last_call.started_at,
            updated_at: result.last_call.started_at,
          } : null

          if (callContextRef.current?.clientSourceId !== sourceId || useAuthStore.getState().user?.id !== actorId) return
          // Update context with lead info
          callContextRef.current = {
            ...callContextRef.current!,
            leadId: result.id,
            leadName: result.full_name,
          }
        }
      } catch {
        // Lookup failed, continue without lead info
      }

      if (callContextRef.current?.clientSourceId !== sourceId || useAuthStore.getState().user?.id !== actorId) return
      setIncomingCallInfo({
        number: callerNumber,
        direction: data.direction,
        uniqueid: data.uniqueid,
        linkedid: data.linkedid,
        extension: data.extension,
        lead: leadInfo,
        lastCall: lastCallInfo,
      })
    })

    const cleanupEnded = onCallEnded((data: CallEventPayload) => {
      console.log("[TelephonySocket] Call ended:", data)
      // An end event cannot establish which browser call it belongs to. In the
      // task pilot, keep the client source identity instead of attaching a late
      // previous call's PBX id. Legacy clinics retain the existing CDR behavior.
      const taskCallbacks = user?.feature_flags?.tasks_enabled === true && user?.feature_flags?.lead_follow_through_enabled === true
      if (!taskCallbacks && callContextRef.current && data.uniqueid) {
        callContextRef.current.uniqueid = data.uniqueid
      }
    })

    // Campaign screen pop (fleet-wide, desk mode included): the sidecar tells
    // ONLY the agent who answered a campaign_q* call which lead it matched.
    // Jump straight to the lead; if the agent is mid-edit (open dialog or a
    // field with text in it), leave them alone and offer a one-click open.
    const cleanupCampaignConnect = onCampaignAgentConnect((data) => {
      if (!data.leadId) {
        toast.info(t("telephonyProvider.campaignCallNoLead", { number: data.leadNumber }), { duration: 15000 })
        return
      }
      const target = `/leads/${data.leadId}`
      if (pathnameRef.current === target || pathnameRef.current?.startsWith(`${target}/`)) return
      if (hasUnsavedWork()) {
        toast.info(t("telephonyProvider.campaignCallConnected", { number: data.leadNumber }), {
          description: t("telephonyProvider.campaignCallUnsavedHint"),
          action: { label: t("telephonyProvider.openLead"), onClick: () => router.push(target) },
          duration: 60000,
        })
        return
      }
      router.push(target)
    })

    const cleanupMissed = onCallMissed((data: CallEventPayload) => {
      console.log("[TelephonySocket] Missed call:", data)
      // Show in-app toast for missed call
      toast.warning(t("telephonyProvider.missedCallFrom", { from: data.from || t("telephonyProvider.unknownCaller") }), {
        description: t("telephonyProvider.tapToView"),
        action: {
          label: tc("view"),
          onClick: () => router.push('/call-analytics'),
        },
        duration: 10000,
      })
      // Increment badge immediately (optimistic update)
      useMissedCallsStore.getState().increment()
    })

    return () => {
      leaveExtension(extension)
      disconnectTelephonySocket()
      cleanupConnect()
      cleanupDisconnect()
      cleanupIncoming()
      cleanupEnded()
      cleanupMissed()
      cleanupCampaignConnect()
    }
  }, [user?.sip_extension, user?.id, user?.feature_flags?.tasks_enabled, user?.feature_flags?.lead_follow_through_enabled, telephonyEnabled, router, t, tc])

  // Mute control
  const toggleMute = useCallback(() => {
    const simpleUser = simpleUserRef.current
    if (!simpleUser) return

    try {
      if (isMuted) {
        simpleUser.unmute()
      } else {
        simpleUser.mute()
      }
      setIsMuted(!isMuted)
    } catch (error) {
      console.error("Failed to toggle mute:", error)
    }
  }, [isMuted])

  // Hold control
  const hold = useCallback(async () => {
    const simpleUser = simpleUserRef.current
    if (!simpleUser || isHeld) return

    try {
      await simpleUser.hold()
    } catch (error) {
      console.error("Failed to hold call:", error)
      toast.error("Failed to hold call")
    }
  }, [isHeld])

  // Unhold control
  const unhold = useCallback(async () => {
    const simpleUser = simpleUserRef.current
    if (!simpleUser || !isHeld) return

    try {
      await simpleUser.unhold()
    } catch (error) {
      console.error("Failed to resume call:", error)
      toast.error("Failed to resume call")
    }
  }, [isHeld])

  // Transfer: open dialog
  const onTransfer = useCallback(() => {
    setShowTransferDialog(true)
  }, [])

  // Blind transfer via SIP REFER(URI)
  const blindTransfer = useCallback(async (extension: string, name: string) => {
    const simpleUser = simpleUserRef.current
    if (!simpleUser || !credentials) return
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const session = (simpleUser as any).session
    if (!session) {
      toast.error(t("telephonyProvider.noActiveSession"))
      return
    }

    const targetUri = UserAgent.makeURI(`sip:${extension}@${credentials.sip_domain}`)
    if (!targetUri) {
      toast.error(t("telephonyProvider.invalidTransferTarget"))
      return
    }

    setTransferState({ type: "blind", targetExtension: extension, targetName: name, status: "completing" })
    setShowTransferDialog(false)
    try {
      await session.refer(targetUri)
      toast.success(t("telephonyProvider.callTransferredTo", { name }))
    } catch (error) {
      console.error("Blind transfer failed:", error)
      toast.error(t("telephonyProvider.transferFailed"))
      setTransferState(null)
    }
  }, [credentials, t])

  // Attended transfer: hold original, dial consultation
  const startAttendedTransfer = useCallback(async (extension: string, name: string) => {
    const simpleUser = simpleUserRef.current
    if (!simpleUser || !credentials) return

    setShowTransferDialog(false)
    setTransferState({ type: "attended", targetExtension: extension, targetName: name, status: "calling" })

    try {
      // Hold original call first
      await simpleUser.hold()

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const session = (simpleUser as any).session
      if (!session) {
        toast.error(t("telephonyProvider.noActiveSession"))
        setTransferState(null)
        return
      }

      const targetUri = UserAgent.makeURI(`sip:${extension}@${credentials.sip_domain}`)
      if (!targetUri) {
        toast.error(t("telephonyProvider.invalidTransferTarget"))
        await simpleUser.unhold()
        setTransferState(null)
        return
      }

      const inviter = new Inviter(session.userAgent, targetUri)
      consultationSessionRef.current = inviter

      inviter.stateChange.addListener((state: SessionState) => {
        if (state === SessionState.Established) {
          setTransferState((prev) => prev ? { ...prev, status: "consulting" } : null)
          setCallState("consulting")
        }
        if (state === SessionState.Terminated) {
          consultationSessionRef.current = null
          const currentTransfer = transferStateRef.current
          if (!currentTransfer || currentTransfer.status !== "completing") {
            // Target didn't answer or hung up -- return to original caller
            simpleUserRef.current?.unhold().catch(() => {})
            setCallState("in-call")
            setTransferState(null)
          }
        }
      })

      await inviter.invite()
    } catch (error) {
      console.error("Attended transfer failed:", error)
      toast.error(t("telephonyProvider.transferFailed"))
      simpleUserRef.current?.unhold().catch(() => {})
      consultationSessionRef.current = null
      setTransferState(null)
      setCallState("in-call")
    }
  }, [credentials, t])

  // Complete attended transfer: REFER original session to consultation session
  const completeAttendedTransfer = useCallback(async () => {
    const simpleUser = simpleUserRef.current
    if (!simpleUser) return

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const originalSession = (simpleUser as any).session
    const consultationSession = consultationSessionRef.current

    if (!originalSession || !consultationSession) {
      toast.error(t("telephonyProvider.cannotCompleteTransfer"))
      return
    }

    setTransferState((prev) => prev ? { ...prev, status: "completing" } : null)
    try {
      await originalSession.refer(consultationSession)
      toast.success(t("telephonyProvider.transferCompleted"))
    } catch (error) {
      console.error("Complete attended transfer failed:", error)
      toast.error(t("telephonyProvider.transferFailed"))
      setTransferState(null)
    }
  }, [t])

  // Cancel attended transfer: end consultation, unhold original
  const cancelAttendedTransfer = useCallback(async () => {
    const consultation = consultationSessionRef.current
    if (consultation) {
      try {
        if (consultation.state === SessionState.Established) {
          await consultation.bye()
        } else if (consultation.state === SessionState.Establishing || consultation.state === SessionState.Initial) {
          await consultation.cancel()
        }
      } catch {
        // ignore -- already terminated
      }
    }
    consultationSessionRef.current = null

    try {
      await simpleUserRef.current?.unhold()
    } catch {
      // ignore
    }

    setTransferState(null)
    setCallState("in-call")
    toast.info(t("telephonyProvider.transferCancelled"))
  }, [t])

  const call = useCallback(
    async (number: string, leadContext?: { leadId: string; leadName?: string }) => {
      const simpleUser = simpleUserRef.current
      if (!simpleUser || !credentials) {
        toast.error(t("telephonyProvider.webrtcNotConnected"))
        return
      }
      const domain = credentials.sip_domain
      if (!domain) {
        toast.error(t("telephonyProvider.missingSipDomain"))
        return
      }
      const stripped = number.replace(/\D+/g, "")
      if (!stripped) {
        toast.error(t("telephonyProvider.invalidPhoneNumber"))
        return
      }

      // Prepend the selected provider's trusted numeric prefix on the in-browser
      // (WebRTC) dial path. For desk mode the backend prepends server-side, so
      // this client-side duplication is WebRTC-only. digits + digits survives
      // all downstream normalization (matches the backend contract).
      const provider = selectedProviderId
        ? providers.find((p) => p.id === selectedProviderId)
        : undefined
      const prefix =
        provider && /^[0-9]+$/.test(provider.prefix) ? provider.prefix : ""
      const normalized = `${prefix}${stripped}`

      // Keep the selected lead and a fresh per-call source identity through hangup.
      callContextRef.current = {
        clientSourceId: crypto.randomUUID(),
        actorId: user?.id,
        leadId: leadContext?.leadId,
        leadName: leadContext?.leadName,
        direction: "outbound",
        number: normalized,
        extension: user?.sip_extension || undefined,
      }

      setCallState("connecting")
      setIncomingNumber(normalized)
      try {
        await simpleUser.call(`sip:${normalized}@${domain}`)
      } catch (error) {
        console.error(error)
        callContextRef.current = null
        setCallState("idle")
        toast.error(t("telephonyProvider.placeCallFailed"))
      }
    },
    [credentials, user?.id, user?.sip_extension, providers, selectedProviderId, t]
  )

  const hangup = useCallback(async () => {
    const simpleUser = simpleUserRef.current
    if (!simpleUser) return
    try {
      await simpleUser.hangup()
      // onCallHangup delegate will handle state cleanup and disposition
    } catch (error) {
      console.error(error)
      setCallState("idle")
      setIncomingNumber(null)
      setIsMuted(false)
    }
  }, [])

  const answer = useCallback(async () => {
    const simpleUser = simpleUserRef.current
    if (!simpleUser) return
    try {
      // Access the underlying session to check state before answering
      const session = (simpleUser as unknown as { session?: { state?: string; accept?: (options?: unknown) => Promise<void> } }).session
      if (session && session.state === SessionState.Establishing) {
        // Session is already being established (media negotiation in progress), just update UI
        console.log("[Telephony] Session already establishing, updating UI")
        setCallState("in-call")
        setIncomingCallInfo(null)
        return
      }
      await simpleUser.answer()
      setCallState("in-call")
      setIncomingCallInfo(null) // Clear popup
    } catch (error) {
      console.error(error)
      toast.error(t("telephonyProvider.answerCallFailed"))
    }
  }, [t])

  const dismissDisposition = useCallback(() => {
    if (callbackTasksEnabled) setPendingDispositions((pending) => pending.slice(1))
    else { setShowDispositionModal(false); setLastCallContext(null) }
  }, [callbackTasksEnabled])

  const value: TelephonyContextValue = {
    status,
    callState,
    incomingNumber,
    call,
    hangup,
    answer,
    hold,
    unhold,
    onTransfer,
    isReady: status === "registered",
    isMuted,
    isHeld,
    toggleMute,
    incomingCallInfo,
    socketConnected,
    showDispositionModal,
    lastCallContext,
    dismissDisposition,
    transferState,
    completeAttendedTransfer,
    cancelAttendedTransfer,
    providers,
    selectedProviderId,
    setSelectedProviderId,
    monitoring,
    stopMonitoring: hangup,
  }

  return (
    <TelephonyContext.Provider value={value}>
      {children}
      <audio ref={remoteAudioRef} autoPlay />

      {/* Enriched incoming call popup (from Socket.IO event) */}
      {incomingCallInfo && callState === "ringing" && (
        <IncomingCallPopup
          callInfo={incomingCallInfo}
          onAnswer={answer}
          onDecline={hangup}
        />
      )}

      {/* Fallback ringing popup (SIP only, no socket info) */}
      {!incomingCallInfo && callState === "ringing" && (
        <div className="fixed bottom-6 right-6 z-50 w-80 rounded-xl border bg-background p-4 shadow-lg">
          <div className="space-y-3">
            <div>
              <p className="text-xs text-muted-foreground">{t("telephonyProvider.incomingCall")}</p>
              <p className="text-sm font-medium">{incomingNumber || t("telephonyProvider.unknownCaller")}</p>
            </div>
            <div className="flex gap-2">
              <Button className="flex-1" onClick={answer}>
                {t("telephonyProvider.answer")}
              </Button>
              <Button variant="outline" className="flex-1" onClick={hangup}>
                {t("telephonyProvider.decline")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Supervisor monitoring banner (WebRTC spy leg auto-answered) */}
      {monitoring && (
        <div className="fixed bottom-6 right-6 z-50 w-80 rounded-xl border bg-background p-4 shadow-lg">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">
                {t("telephonyProvider.monitoring")}
              </p>
              <p className="truncate text-sm font-medium">
                {t("telephonyProvider.monitoringTarget", {
                  extension: monitoring.extension,
                  // Static keys (not a template) so next-intl key typing holds;
                  // an unrecognised mode falls back to the raw wire value.
                  mode:
                    monitoring.mode === "listen"
                      ? t("telephonyProvider.monitorMode.listen")
                      : monitoring.mode === "whisper"
                        ? t("telephonyProvider.monitorMode.whisper")
                        : monitoring.mode === "barge"
                          ? t("telephonyProvider.monitorMode.barge")
                          : monitoring.mode,
                })}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={hangup}>
              {t("telephonyProvider.stopMonitoring")}
            </Button>
          </div>
        </div>
      )}

      {/* Transfer target selection dialog */}
      <TransferDialog
        open={showTransferDialog}
        onClose={() => setShowTransferDialog(false)}
        onBlindTransfer={blindTransfer}
        onAttendedTransfer={startAttendedTransfer}
        currentExtension={user?.sip_extension || ""}
      />

      {/* In-call floating control bar */}
      <InCallControls />

      {/* Post-call disposition modal */}
      {showDispositionModal && lastCallContext && (
        <PostCallDispositionModal
          open={showDispositionModal}
          onClose={dismissDisposition}
          callContext={lastCallContext}
        />
      )}
    </TelephonyContext.Provider>
  )
}
